import React, { useCallback, useMemo } from "react";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router-dom";

import {
	Breadcrumbs,
	Button,
	CollapseSection,
	MainContainer,
	UnderwritingBreadcrumbs,
} from "components";
import { LineDivider } from "components/layout";
import { breadcrumbsReportFromMerchantPart } from "helpers";
import {
	getPotentialsMatches,
	getResultTableData,
	gOfacDataMapAuroraOutcome,
	LABELS_GOFAC_RESULT_DBA_NAME,
	LABELS_GOFAC_RESULT_LEGAL_NAME,
	LABELS_MATCHES,
} from "mappers";
import {
	useDownloadGofacDocumentMutation,
	useGofacAuroraOutcomeFieldMutation,
	useGofacResultQuery,
} from "queries";
import {
	getCurrentUserAccessToken,
	getCurrentUserId,
} from "store/user/user.reducer";
import { UnderwritingTabs, UnderwritingRouteMatchProps } from "types";

import ReportTable from "./reportTable";

import styles from "./reports.module.scss";

const MerchantGofacReport = () => {
	const currentUserId = useSelector(getCurrentUserId);
	const token: string = useSelector(getCurrentUserAccessToken);
	const match: UnderwritingRouteMatchProps = useRouteMatch();

	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/") || false,
		[match?.url],
	);

	const applicationId = useMemo(
		() =>
			isMerchantPage ? match?.params?.applicationId : match?.params?.id,
		[isMerchantPage, match?.params?.applicationId, match?.params?.id],
	);

	const {
		data: dataMerchant,
		isFetching,
		refetch: dataMerchantRefetch,
	} = useGofacResultQuery({
		id: applicationId,
	});

	const {
		mutate: downloadGOFACDocument,
		isLoading: isLoadingDownloadDocument,
	} = useDownloadGofacDocumentMutation();

	const downloadDocument = () =>
		downloadGOFACDocument({
			id: applicationId,
			token: token,
		});

	const {
		mutate: submitGofacAuroraOutcome,
		isLoading: isLoadingGofacAuroraOutcome,
	} = useGofacAuroraOutcomeFieldMutation();

	const gOFACAuroraOutcomeSubmitHandler = useCallback(
		values =>
			submitGofacAuroraOutcome({
				id: applicationId,
				values,
				onSuccessHandler: dataMerchantRefetch,
			}),
		[applicationId, dataMerchantRefetch, submitGofacAuroraOutcome],
	);

	const merchantDbaNameResultData = getResultTableData(
		gOfacDataMapAuroraOutcome({
			data: dataMerchant?.dba_name?.original,
			auroraOutcome: dataMerchant?.original_outcome,
		}),
		gOfacDataMapAuroraOutcome({
			data: dataMerchant?.dba_name?.rerun,
			auroraOutcome: dataMerchant?.rerun_outcome,
		}),
		LABELS_GOFAC_RESULT_DBA_NAME,
		"potential_matches",
	);
	const merchantLegalNameResultData = getResultTableData(
		gOfacDataMapAuroraOutcome({
			data: dataMerchant?.legal_name?.original,
			auroraOutcome: dataMerchant?.original_outcome,
		}),
		gOfacDataMapAuroraOutcome({
			data: dataMerchant?.legal_name?.rerun,
			auroraOutcome: dataMerchant?.rerun_outcome,
		}),
		LABELS_GOFAC_RESULT_LEGAL_NAME,
		"potential_matches",
	);
	const dbaNamePotentialsMatches = getPotentialsMatches(
		dataMerchant?.dba_name,
		LABELS_MATCHES,
	);
	const legalNamePotentialsMatches = getPotentialsMatches(
		dataMerchant?.legal_name,
		LABELS_MATCHES,
	);
	const isDbaNameRerun = useMemo(
		() => !!dataMerchant?.dba_name?.rerun,
		[dataMerchant?.dba_name?.rerun],
	);
	const isLegalNameRerun = useMemo(
		() => !!dataMerchant?.legal_name?.rerun,
		[dataMerchant?.legal_name?.rerun],
	);

	const editableFields = useMemo(() => {
		let fields = {};
		if (
			!isMerchantPage &&
			match?.params?.tab !== UnderwritingTabs.Unassigned.name &&
			match?.params?.tab !== UnderwritingTabs.Historical.name &&
			match?.params?.tab !== UnderwritingTabs.QAUnassigned.name &&
			match?.params?.tab !== UnderwritingTabs.QAComplete.name &&
			dataMerchant?.underwriter_id &&
			dataMerchant?.underwriter_id === currentUserId
		) {
			fields = {
				...fields,
				aurora_outcome: {
					submitHandler: gOFACAuroraOutcomeSubmitHandler,
					isLoading: isLoadingGofacAuroraOutcome,
					reactSelectProps: { menuPlacement: "top" },
				},
			};
		}
		return fields;
	}, [
		currentUserId,
		dataMerchant?.underwriter_id,
		gOFACAuroraOutcomeSubmitHandler,
		isLoadingGofacAuroraOutcome,
		isMerchantPage,
		match?.params?.tab,
	]);

	return (
		<MainContainer>
			{isMerchantPage ? (
				<Breadcrumbs
					pages={breadcrumbsReportFromMerchantPart({
						id: match.params.id,
						title: "gOFAC Result - Merchant",
					})}
				/>
			) : (
				// <MerchantBreadcrumbs />
				<UnderwritingBreadcrumbs match={match} />
			)}
			<p className={clsx(styles.tableTitle, isFetching && "mb-0")}>
				DBA Name Result
			</p>
			<ReportTable
				data={merchantDbaNameResultData}
				isRerun={isDbaNameRerun}
				isLoading={isFetching}
				editableFields={editableFields}
			/>

			{dbaNamePotentialsMatches?.length !== 0 && !isFetching ? (
				<>
					<p className="text-muted mt-4 mb-3">
						List of Potentials Matches
					</p>

					{dbaNamePotentialsMatches?.map((item, key) => {
						return (
							<CollapseSection
								className={styles.collapseWrapper}
								classNameBody={styles.collapseBody}
								header={item?.name}
								key={key}
								children={
									<ReportTable
										data={item?.items}
										disableHeader
										bordered="horizontal"
										isRerun={isDbaNameRerun}
									/>
								}
							/>
						);
					})}
				</>
			) : null}

			<p className={clsx(styles.tableTitle, isFetching && "mb-0")}>
				Legal Name Result
			</p>
			<ReportTable
				data={merchantLegalNameResultData}
				isRerun={isLegalNameRerun}
				isLoading={isFetching}
				editableFields={editableFields}
			/>

			{legalNamePotentialsMatches?.length !== 0 && !isFetching ? (
				<>
					<p className="text-muted mt-4 mb-3">
						List of Potentials Matches
					</p>

					{legalNamePotentialsMatches?.map((item, key) => {
						return (
							<CollapseSection
								className={styles.collapseWrapper}
								classNameBody={styles.collapseBody}
								header={item?.name}
								key={key}
								children={
									<ReportTable
										data={item?.items}
										disableHeader
										bordered="horizontal"
										isRerun={isLegalNameRerun}
									/>
								}
							/>
						);
					})}
				</>
			) : null}

			<LineDivider />
			<Button
				type="button"
				value="Download"
				onClick={downloadDocument}
				isLoading={isFetching || isLoadingDownloadDocument}
			/>
		</MainContainer>
	);
};

export default MerchantGofacReport;
